import { useEffect, useMemo, useRef, useState } from 'react';
import { SearchIcon } from 'lucide-react';
import { useDebounceFn } from 'ahooks';
import { cn } from '@rocco/utils/cn';
import { Input } from '@rocco/components/input';
import { ScrollArea } from '@rocco/components/scroll-area';
import type { MeUser } from '~/server/types';
import type { BuildHubProject, Conversation } from '../../types';
import { useConversationFilters } from '../../hooks/use-conversation-filters';
import { useConversations } from '../../hooks/use-conversations';
import { filterChats, type ConversationStatusFilter } from '../../utils';
import { ChatInboxFilter } from './ChatInboxFilter';
import {
    ChatInboxListItem,
    ChatInboxListItemSkeleton,
} from './ChatInboxListItem';

/**
 * The chat inbox list. This is an infinite scrolling list of conversations
 * in the current user's personal inbox or company inbox.
 * @see {@link ChatInboxDrawer}
 */
export function ChatInboxList({
    className,
    conversations,
    user,
    professionalID,
    selectedConversationID,
    isLoading = false,
    hasMore = false,
    showDraftIcon,
    onSelectConversation,
    onLoadMore,
}: {
    className?: string;
    conversations: Conversation[];
    user: MeUser;
    professionalID: number | null;
    selectedConversationID?: number | null;
    isLoading?: boolean;
    hasMore?: boolean;
    showDraftIcon?: (
        conversation: Conversation,
        professionalID: number | null,
    ) => boolean;
    onSelectConversation?: (
        conversation: Conversation,
        professionalID: number | null,
    ) => void;
    onLoadMore?: () => void;
}) {
    const scrollAreaRef = useRef<HTMLDivElement>(null);
    const loadMoreSentinelRef = useRef<HTMLDivElement>(null);
    const { search, buildHubIDs, statusFilter, setParams } = useConversations();
    const { filters } = useConversationFilters();
    const [localSearch, setLocalSearch] = useState(search);
    const selectedProjects = useMemo(() => {
        return (
            filters?.projects.filter((project) =>
                buildHubIDs.includes(project.ID),
            ) ?? []
        );
    }, [filters?.projects, buildHubIDs]);
    const filteredConversations = useMemo(() => {
        return filterChats({
            conversations,
            userID: user.ID,
            professionalID,
            search: localSearch,
            projectIDs: buildHubIDs,
            statusFilter,
        });
    }, [
        conversations,
        user.ID,
        professionalID,
        localSearch,
        buildHubIDs,
        statusFilter,
    ]);
    const { run: setSearchDebounced, flush: flushSearch } = useDebounceFn(
        (search: string) => setParams({ search }),
        { wait: 300 },
    );

    function handleStatusFilterChange(status: ConversationStatusFilter) {
        flushSearch();
        setParams({ statusFilter: status });
    }

    function handleProjectsChange(projects: BuildHubProject[]) {
        flushSearch();
        setParams({ buildHubIDs: projects.map((project) => project.ID) });
    }

    // Keep the input in sync when shared search changes externally.
    useEffect(() => {
        setLocalSearch(search);
    }, [search]);

    // Scroll to top when the filters change.
    useEffect(() => {
        scrollAreaRef.current?.scrollTo({
            top: 0,
            behavior: 'instant',
        });
    }, [localSearch, statusFilter, buildHubIDs]);

    // Trigger load more conversations when the user scrolls to the bottom.
    useEffect(() => {
        const sentinel = loadMoreSentinelRef.current;
        const scrollArea = scrollAreaRef.current;
        if (!sentinel || !scrollArea || !hasMore || isLoading) {
            return;
        }
        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                if (
                    entry?.isIntersecting &&
                    onLoadMore &&
                    hasMore &&
                    !isLoading
                ) {
                    onLoadMore();
                }
            },
            { root: scrollArea },
        );
        observer.observe(sentinel);

        return () => {
            observer.disconnect();
        };
    }, [hasMore, isLoading, onLoadMore]);

    return (
        <section className={cn('flex flex-col gap-6', className)}>
            <div className="px-2">
                <Input
                    className="h-12 rounded-full px-12 font-normal placeholder:font-normal md:text-4!"
                    containerClassName="min-w-0 flex-1"
                    color="light"
                    placeholder="Search messages"
                    prefix={<SearchIcon className="size-5 opacity-40" />}
                    postfix={
                        <ChatInboxFilter
                            className="pointer-events-auto translate-x-1.5"
                            statusFilter={statusFilter}
                            selectedProjects={selectedProjects}
                            onStatusFilterChange={handleStatusFilterChange}
                            onProjectsChange={handleProjectsChange}
                        />
                    }
                    value={localSearch}
                    onChange={(e) => {
                        setLocalSearch(e.target.value);
                        setSearchDebounced(e.target.value.trim());
                        if (!e.target.value.trim()) {
                            flushSearch();
                        }
                    }}
                />
            </div>
            <ScrollArea
                ref={scrollAreaRef}
                className={cn(
                    'min-h-0 flex-1',
                    // Fix for width overflow issue.
                    // https://github.com/radix-ui/primitives/issues/2722
                    '[&>div>div]:block!',
                )}
                scrollBarClassName="w-2 translate-x-2"
                thumbClassName="bg-gray-500"
                overscroll="contain"
            >
                <ul
                    className="relative flex flex-col divide-y divide-gray-300"
                    aria-label="Chat inbox"
                    aria-live="polite"
                    aria-busy={isLoading}
                >
                    {filteredConversations.length === 0 && isLoading
                        ? Array.from({ length: 8 }).map((_, index) => (
                              <ChatInboxListItemSkeleton key={index} />
                          ))
                        : filteredConversations.map((conversation) => (
                              <ChatInboxListItem
                                  key={conversation.ID}
                                  conversation={conversation}
                                  professionalID={professionalID}
                                  showDraftIcon={showDraftIcon?.(
                                      conversation,
                                      professionalID,
                                  )}
                                  isSelected={
                                      selectedConversationID ===
                                      Number(conversation.ID)
                                  }
                                  onSelect={() =>
                                      onSelectConversation?.(
                                          conversation,
                                          professionalID,
                                      )
                                  }
                              />
                          ))}
                    {hasMore && filteredConversations.length > 0 && (
                        <div
                            ref={loadMoreSentinelRef}
                            className="invisible absolute inset-x-0 bottom-120 h-px"
                            aria-hidden="true"
                        ></div>
                    )}
                </ul>
            </ScrollArea>
        </section>
    );
}
